import {
  getCarePlan,
  getUsername,
  getSihAccId,
  getUsersSihAccId,
} from "../services/wizard.service";
import { addAssignedClient } from "./assignedClient";
import { CarePlanUsersAssociate } from "../constants";
import { toggleLoading } from "./loading";
import { observable } from "../utils/observer";
import { getUsersBilling } from "../services/getUsersBilling.service";

const careCircleUsersFilter = (users, type) => {
  if (type === CarePlanUsersAssociate.HPC) {
    return users.sort((a, b) => a.sequenceNo - b.sequenceNo);
  } else {
    return users
      .filter((item) => item.associationTypeCode === type)
      .sort((a, b) => a.sequenceNo - b.sequenceNo);
  }
};

const assignedClientMapper = (carePlan) => {
  const { name, address, dob, email, phone, group, clientComment } = carePlan;
  const carePlanId = carePlan?.carePlanId ?? carePlan?.carePlanID;

  return {
    carePlanId,
    name,
    address,
    dob,
    email,
    phone,
    group,
    clientComment,
  };
};

const supportPlanGroupUsersMapper = (carePlan) => {
  const { carePlanGroupUsers } = carePlan;
  return carePlanGroupUsers;
};

const caseManagerMapper = (carePlan) => {
  const { careCircleUsers } = carePlan;
  return careCircleUsersFilter(
    careCircleUsers,
    CarePlanUsersAssociate.CaseManager,
  );
};

const guardianMapper = (carePlan) => {
  const { careCircleUsers } = carePlan;
  return careCircleUsersFilter(
    careCircleUsers,
    CarePlanUsersAssociate.Guardian,
  );
};

const providerMapper = (carePlan) => {
  const { careCircleHpcs } = carePlan;
  return careCircleUsersFilter(careCircleHpcs, CarePlanUsersAssociate.HPC);
};

const naturalSupportMapper = (carePlan) => {
  const { careCircleUsers } = carePlan;
  return careCircleUsersFilter(
    careCircleUsers,
    CarePlanUsersAssociate.NaturalSupport,
  );
};

const countyBoardMapper = (carePlan) => {
  const {
    ddBoardID,
    countyOnCall,
    countyPhone,
    muiEmail,
    muiPhone,
    protectedService,
    muiLink,
  } = carePlan;
  return {
    ddBoardID,
    countyOnCall,
    countyPhone,
    muiEmail,
    muiPhone,
    protectedService,
    muiLink,
  };
};

const callTreeMapper = (carePlan) => {
  const { careCircleUsers } = carePlan;
  return careCircleUsersFilter(
    careCircleUsers,
    CarePlanUsersAssociate.CallTree,
  ).sort((a, b) => a.sequenceNo - b.sequenceNo);
};

const csrMapper = (carePlan) => {
  const { careCircleUsers } = carePlan;
  return careCircleUsersFilter(careCircleUsers, CarePlanUsersAssociate.CSR);
};

const emergencyPlanMapper = (carePlan) => {
  const { emergencyContacts, disasterDetails } = carePlan;
  return {
    contacts: emergencyContacts,
    disasterDetails,
  };
};

const personalSupportDetailsMapper = (carePlan) => {
  const {
    criticalClientInformation,
    personalDetails,
    supportDetails,
    whatToCareNoteFor,
  } = carePlan;
  return {
    criticalClientInformation,
    personalDetails,
    supportDetails,
    whatToCareNoteFor,
  };
};

const healthInformationDetailsMapper = (carePlan) => {
  const { diagnosis, allergies } = carePlan;
  return {
    diagnosis,
    allergies,
  };
};

const alertConditionsDetailsMapper = (carePlan) => {
  const { alertConditions } = carePlan;
  return {
    alertConditions,
  };
};

const technologyDetailsMapper = (carePlan) => {
  const { technologyDetails } = carePlan;
  return {
    technologyDetails,
  };
};

const calendarEffectiveDateDateMapper = (carePlan) => {
  const { calendarEffectiveDate } = carePlan;
  return calendarEffectiveDate;
};

const usersBillingDataMapper = (billingData) => {
  const {
    userID,
    siHAccId,
    userTypeCode,
    serviceTypeCode,
    timeZoneCode,
    installDate,
    supportStart,
  } = billingData;
  return {
    userID,
    siHAccId,
    userTypeCode,
    serviceTypeCode,
    timeZoneCode,
    installDate,
    supportStart,
  };
};

const populateWizardStore = (carePlan) => (dispatch) => {
  // Step1: Assigned Client
  const assignedClient = assignedClientMapper(carePlan);
  dispatch(addAssignedClient(assignedClient));
  // Step2: Care Circle
  // case manager
  const caseManager = caseManagerMapper(carePlan);
  dispatch({ type: "CASE_MANAGERS_SUCCESS", payload: caseManager });
  // guardian
  const guardians = guardianMapper(carePlan);
  dispatch({ type: "GUARDIANS_SUCCESS", payload: guardians });
  // provider
  const provider = providerMapper(carePlan);
  dispatch({ type: "HPC_SUCCESS", payload: provider });
  // natural support
  const naturalSupport = naturalSupportMapper(carePlan);
  dispatch({ type: "NATURAL_SUPPORT_SUCCESS", payload: naturalSupport });
  // county board
  const countyBoard = countyBoardMapper(carePlan);
  dispatch({ type: "COUNTY_DETAILS_SUCCESS", payload: countyBoard });
  // call tree
  const callTree = callTreeMapper(carePlan);
  dispatch({ type: "CALL_TREE_SUCCESS", payload: callTree });

  // Step3: CSR
  const csr = csrMapper(carePlan);
  dispatch({ type: "CSR_SUCCESS", payload: csr });

  // Step4: Emergency Planning
  const plan = emergencyPlanMapper(carePlan);
  dispatch({ type: "SET_EMERGENCY_PLAN", payload: plan });

  // Step5: Personal Support Details
  const details = personalSupportDetailsMapper(carePlan);
  dispatch({ type: "PS_DETAILS_SUCCESS", payload: details });

  // Step8: Alert Conditions Details
  const acDetails = alertConditionsDetailsMapper(carePlan);
  dispatch({ type: "AC_DETAILS_SUCCESS", payload: acDetails });

  // Step9: Health Information Details
  const hiDetails = healthInformationDetailsMapper(carePlan);
  dispatch({ type: "HI_DETAILS_SUCCESS", payload: hiDetails });

  // Step10: Technology Details
  const technologyDetails = technologyDetailsMapper(carePlan);
  dispatch({ type: "TECH_DETAILS_SUCCESS", payload: technologyDetails });

  // subscribe/set care plan data
  observable.subscribe(carePlan);

  return Promise.resolve();
};

const usersBillingStore = (billingData) => (dispatch) => {
  const usersBillingDetails = usersBillingDataMapper(billingData);
  dispatch({
    type: "USERS_BILLING_DATA_SUCCESS",
    payload: usersBillingDetails,
  });
  return Promise.resolve();
};

const getBillingData = (userId) => (dispatch) => {
  dispatch(toggleLoading(true));
  return getUsersBilling(userId)
    .then((billingData) => {
      dispatch(usersBillingStore(billingData)).then(() => {
        dispatch(toggleLoading(false));
      });
      dispatch({ type: "USERS_BILLING_DATA_SUCCESS", payload: billingData });
      return Promise.resolve(billingData);
    })
    .catch((err) => {
      dispatch(toggleLoading(false));
      dispatch({ type: "USERS_BILLING_DATA_ERROR", payload: err.message });
    });
};

const getCarePlanData = (userId, handlegetCarePlanError) => (dispatch) => {
  dispatch(toggleLoading(true));
  return getCarePlan({ userId })
    .then((carePlan) => {
      dispatch(populateWizardStore(carePlan.data)).then(() => {
        dispatch(toggleLoading(false));
      });
      dispatch({ type: "GET_CARE_PLAN_SUCCESS", payload: carePlan.data });
      return Promise.resolve(carePlan.data);
    })
    .catch((err) => {
      dispatch(toggleLoading(false));
      dispatch({ type: "GET_CARE_PLAN_ERROR", payload: err.message });
      handlegetCarePlanError();
    });
};

export const getUsernames = () => (dispatch) => {
  dispatch(toggleLoading(true));

  return getUsername()
    .then((response) => {
      dispatch({ type: "GET_USERNAMES_SUCCESS", payload: { response } });
      dispatch(toggleLoading(false));
      return Promise.resolve(response.data);
    })
    .catch((error) => {
      dispatch(toggleLoading(false));
      dispatch({ type: "GET_USERNAMES_ERROR", payload: error.message });
      return Promise.reject(error);
    });
};

export const getSihAccIds = () => (dispatch) => {
  dispatch(toggleLoading(true));

  return getSihAccId()
    .then((response) => {
      dispatch({ type: "GET_IDS_SUCCESS", payload: { response } });
      dispatch(toggleLoading(false));
      return Promise.resolve(response.data);
    })
    .catch((error) => {
      dispatch(toggleLoading(false));
      dispatch({ type: "GET_IDS_ERROR", payload: error.message });
      return Promise.reject(error);
    });
};

export const getUsersSihAccIds = (userId) => (dispatch) => {
  dispatch(toggleLoading(true));

  return getUsersSihAccId(userId)
    .then((response) => {
      dispatch({ type: "GET_ID_SUCCESS", payload: { response } });
      dispatch(toggleLoading(false));
      return Promise.resolve(response.data);
    })
    .catch((error) => {
      dispatch(toggleLoading(false));
      dispatch({ type: "GET_ID_ERROR", payload: error.message });
      return Promise.reject(error);
    });
};

const resetWizardStore = () => async (dispatch) => {
  await dispatch({ type: "ASSIGNED_CLIENT_PROFILE_INIT" });
  await dispatch({ type: "ASSIGNED_CLIENT_INIT" });
  await dispatch({ type: "SUPPORT_PLAN_GROUP_USERS_INIT" });
  await dispatch({ type: "WIZARD_DATA_INIT" });
  await dispatch({ type: "CASE_MANAGERS_INIT" });
  await dispatch({ type: "HPC_INIT" });
  await dispatch({ type: "GUARDIANS_INIT" });
  await dispatch({ type: "COUNTY_DETAILS_INIT" });
  await dispatch({ type: "NATURAL_SUPPORT_INIT" });
  await dispatch({ type: "CALL_TREE_INIT" });
  await dispatch({ type: "CSR_INIT" });
  await dispatch({ type: "PS_DETAILS_INIT" });
  await dispatch({ type: "EMERGENCY_PLAN_INIT" });
  await dispatch({ type: "CPA_ACTIONS_INIT" });
  await dispatch({ type: "CPA_ACTION_EVENTS_INIT" });
  await dispatch({ type: "HI_DETAILS_INIT" });
  await dispatch({ type: "AC_DETAILS_INIT" });
  await dispatch({ type: "TECH_DETAILS_INIT" });
  await dispatch({ type: "CALENDAR_TEMPLATE_INIT" });
  await dispatch({ type: "SCHEDULER_LOADED_WEEKS_INIT" });
  await dispatch({ type: "SCHEDULER_TEMPLATE_INIT" });
  await dispatch({ type: "BULK_ARR_INIT" });
  await dispatch({ type: "CALENDAR_DATE_INIT" });
  await dispatch({ type: "SENSORS_LIST_INIT" });
  await dispatch({ type: "PREDICATES_INIT" });
  await dispatch({ type: "PREDICATE_COMPOSITIONS_INIT" });
  await dispatch({ type: "VISITORS_INIT" });
  await dispatch({ type: "CALENDAR_TEMPLATE_SPECIFIC_DAY_EVENTS_INIT" });
  await dispatch({ type: "CALENDAR_TEMPLATE_SPECIFIC_DAY_VALIDATOR_INIT" });
  await dispatch({ type: "USERS_BILLING_DATA_INIT" });
  await dispatch({ type: "CLIENT_SENSORS_LIST_INIT" });
  await dispatch({ type: "ACKNOWLEDGEMENT_LOGS_INIT" });
  await dispatch({ type: "SATISFACTION_SCORES_INIT" });
  observable.reset();
};

export {
  resetWizardStore,
  getCarePlanData,
  assignedClientMapper,
  caseManagerMapper,
  guardianMapper,
  providerMapper,
  naturalSupportMapper,
  countyBoardMapper,
  callTreeMapper,
  csrMapper,
  emergencyPlanMapper,
  personalSupportDetailsMapper,
  healthInformationDetailsMapper,
  alertConditionsDetailsMapper,
  technologyDetailsMapper,
  getBillingData,
};
